import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { schoolsAPI, campusAPI, usersAPI } from '../api';
import { Campus, User } from '../types';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';

interface UserManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'school' | 'campus';
  entityId: string;
  entityName: string;
  availableCampuses?: Campus[]; // For school management to assign users to specific campuses
}

export function UserManagementDialog({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  availableCampuses = [],
}: UserManagementDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCampusId, setSelectedCampusId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const queryClient = useQueryClient();

  // Fetch assigned users
  const { data: assignedUsersData, isLoading: isLoadingAssigned } = useQuery({
    queryKey: [`${entityType}-users`, entityId],
    queryFn: async () => {
      if (entityType === 'school') {
        return await schoolsAPI.getSchoolUsers(entityId);
      } else {
        return await campusAPI.getCampusUsers(entityId);
      }
    },
    enabled: isOpen,
  });

  // Fetch all users for assignment
  const { data: allUsersData } = useQuery({
    queryKey: ['all-users'],
    queryFn: async () => {
      const response = await usersAPI.getAllUsers();
      return response;
    },
    enabled: isOpen,
  });

  const assignedUsers = assignedUsersData?.data || [];
  const allUsers = allUsersData || [];

  // Filter available users (not already assigned)
  const availableUsers = allUsers.filter(
    (user: User) => !assignedUsers.some((assigned: User) => assigned._id === user._id)
  );

  // Filter users based on search term
  const filteredAvailableUsers = availableUsers.filter((user: User) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Assign user mutation
  const assignUserMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId) throw new Error('Please select a user');
      
      if (entityType === 'school') {
        return await schoolsAPI.assignUserToSchool(entityId, selectedUserId, selectedCampusId || undefined);
      } else {
        return await campusAPI.assignUserToCampus(entityId, selectedUserId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${entityType}-users`, entityId] });
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
      setSuccess('User assigned successfully');
      setSelectedUserId('');
      setSelectedCampusId('');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      setError(error.response?.data?.message || 'Failed to assign user');
      setTimeout(() => setError(''), 5000);
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (entityType === 'school') {
        return await schoolsAPI.removeUserFromSchool(entityId, userId);
      } else {
        return await campusAPI.removeUserFromCampus(entityId, userId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${entityType}-users`, entityId] });
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
      setSuccess('User removed successfully');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      setError(error.response?.data?.message || 'Failed to remove user');
      setTimeout(() => setError(''), 5000);
    },
  });

  const handleAssignUser = () => {
    assignUserMutation.mutate();
  };

  const handleRemoveUser = (userId: string) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      removeUserMutation.mutate(userId);
    }
  };

  const getUserRoleLabel = (role: string | number) => {
    const roleNum = typeof role === 'string' ? parseInt(role, 10) : role;
    const roleStr = String(role).toLowerCase();

    // Check numeric roles first
    if (roleNum === 7891 || roleStr === 'admin') return 'Admin';
    if (roleNum === 7892 || roleStr === 'user') return 'User';
    if (roleNum === 7893 || roleStr === 'moderator') return 'Moderator';

    return 'Unknown';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Manage Users - {entityName}
            <Badge variant="outline" className="ml-2">
              {entityType === 'school' ? 'School' : 'Campus'}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Add User Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Add User</h3>
            
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="p-2">
                      <Input
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mb-2"
                      />
                    </div>
                    {filteredAvailableUsers.map((user: User) => (
                      <SelectItem key={user._id} value={user._id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {entityType === 'school' && availableCampuses.length > 0 && (
                <Select
                  value={selectedCampusId || "none"}
                  onValueChange={(v) => setSelectedCampusId(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select campus (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No campus</SelectItem>
                    {availableCampuses.map((campus) => (
                      <SelectItem key={campus._id} value={campus._id}>
                        {campus.campus_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                onClick={handleAssignUser}
                disabled={!selectedUserId || assignUserMutation.isPending}
              >
                {assignUserMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                <span className="ml-2">Add</span>
              </Button>
            </div>
          </div>

          {/* Notifications */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Assigned Users Table */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">
              Assigned Users ({assignedUsers.length})
            </h3>
            
            {isLoadingAssigned ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : assignedUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No users assigned to this {entityType}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    {entityType === 'school' && <TableHead>Campus</TableHead>}
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignedUsers.map((user: User & { campus?: { campus_name?: string } }) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getUserRoleLabel(user.role)}
                        </Badge>
                      </TableCell>
                      {entityType === 'school' && (
                        <TableCell>
                          {user.campus?.campus_name || '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveUser(user._id)}
                          disabled={removeUserMutation.isPending}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}